import { useSyncExternalStore } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { reportError } from './sentry';

/**
 * The service worker's registration and the "new version ready" state.
 *
 * The worker is built by vite-plugin-pwa (`vite.config.ts`) in prompt mode: a
 * new version installs in the background and then waits. Nothing reloads the
 * page by itself, because a review page holds an open progress stream and
 * forms hold typed work; the update toast (`UpdateToast`) asks first.
 *
 * A tab can stay open for days, so the registration is asked to look for a
 * new worker every hour, whenever the tab comes back into view and when the
 * browser comes back online. The worker file is served `no-cache`
 * (`deploy/Caddyfile`), so each check reaches the origin.
 */

/** How often an open tab checks for a new deploy. */
const UPDATE_CHECK_MS = 60 * 60 * 1000;

let needRefresh = false;
let applyUpdate: ((reloadPage?: boolean) => Promise<void>) | null = null;
const listeners = new Set<() => void>();

function setNeedRefresh(value: boolean): void {
  if (needRefresh === value) return;
  needRefresh = value;
  listeners.forEach((listener) => listener());
}

function watchForUpdates(registration: ServiceWorkerRegistration): void {
  const check = () => {
    // Offline, or mid-install: the next trigger tries again.
    registration.update().catch(() => undefined);
  };
  window.setInterval(check, UPDATE_CHECK_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check();
  });
  window.addEventListener('online', check);
}

/**
 * Registers the service worker once, from `main.tsx`. A no-op in dev (the
 * plugin builds no worker there) and in a browser without service workers.
 */
export function registerPwa(): void {
  if (!('serviceWorker' in navigator)) return;
  applyUpdate = registerSW({
    onNeedRefresh() {
      setNeedRefresh(true);
    },
    onRegisteredSW(_url, registration) {
      if (registration) watchForUpdates(registration);
    },
    onRegisterError(error: unknown) {
      reportError(error, { scope: 'service_worker_register' });
    },
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): boolean {
  return needRefresh;
}

export interface PwaUpdate {
  /** A new version is installed and waiting for the user. */
  needRefresh: boolean;
  /** Activate the waiting worker and reload onto the new version. */
  reload: () => Promise<void>;
  /** Hide the offer until the next new version arrives. */
  dismiss: () => void;
}

export function usePwaUpdate(): PwaUpdate {
  const current = useSyncExternalStore(subscribe, getSnapshot, () => false);
  return {
    needRefresh: current,
    reload: async () => {
      if (applyUpdate) await applyUpdate(true);
    },
    dismiss: () => setNeedRefresh(false),
  };
}
